import React, { useState, useMemo } from 'react';
import {
  Paper,
  Typography,
  Container,
  Grid,
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  TextField,
  InputAdornment,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Divider,
  Alert
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleIcon from '@mui/icons-material/People';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AssignmentIcon from '@mui/icons-material/Assignment';
import TimelineIcon from '@mui/icons-material/Timeline';
import SchoolIcon from '@mui/icons-material/School';

const tutoriais = [
  {
    id: 1,
    titulo: "Cadastro de Usuários",
    arquivo: "VID-20250827-WA0002.mp4",
    descricao: "Aprenda a criar e gerenciar usuários do sistema, definir permissões e controlar acessos.",
    categoria: "Cadastros Básicos",
    duracao: "5:30",
    nivel: "Iniciante",
    icone: <PeopleIcon />,
    topicos: [
      "Criar novo usuário",
      "Definir permissões (Admin/Funcionário)",
      "Editar dados do usuário",
      "Desativar/Ativar usuários"
    ]
  },
  {
    id: 2,
    titulo: "Cadastro de Fornecedores",
    arquivo: "VID-20250827-WA0003.mp4",
    descricao: "Como cadastrar fornecedores de veículos e serviços, incluindo dados de contato e informações fiscais.",
    categoria: "Cadastros Básicos",
    duracao: "4:45",
    nivel: "Iniciante",
    icone: <BusinessIcon />,
    topicos: [
      "Adicionar novo fornecedor",
      "Cadastrar CNPJ e dados fiscais",
      "Gerenciar contatos",
      "Vincular veículos ao fornecedor"
    ]
  },
  {
    id: 3,
    titulo: "Cadastro de Promoções",
    arquivo: "VID-20250827-WA0004.mp4",
    descricao: "Configure promoções especiais como 'Alugue 7 dias e pague 5' para atrair mais clientes.",
    categoria: "Gestão Comercial",
    duracao: "6:15",
    nivel: "Intermediário",
    icone: <LocalOfferIcon />,
    topicos: [
      "Criar promoção personalizada",
      "Definir dias pagos vs. dias ganhos",
      "Ativar/Desativar promoções",
      "Aplicar promoção em aluguéis"
    ]
  },
  {
    id: 4,
    titulo: "Cadastro de Veículos",
    arquivo: "VID-20250827-WA0005.mp4",
    descricao: "Registre veículos da frota com todas as informações necessárias: placa, RENAVAM, valores de diária.",
    categoria: "Cadastros Básicos",
    duracao: "7:20",
    nivel: "Iniciante",
    icone: <DirectionsCarIcon />,
    topicos: [
      "Adicionar novo veículo",
      "Cadastrar placa, RENAVAM e chassi",
      "Definir preços (dia/semana/mês)",
      "Gerenciar status do veículo"
    ]
  },
  {
    id: 5,
    titulo: "Cadastro de Clientes",
    arquivo: "VID-20250827-WA0006.mp4",
    descricao: "Como cadastrar clientes, fazer upload de documentos e consultar histórico de aluguéis.",
    categoria: "Cadastros Básicos",
    duracao: "5:50",
    nivel: "Iniciante",
    icone: <PeopleIcon />,
    topicos: [
      "Cadastrar cliente (CPF/CNPJ)",
      "Upload de documentos (CNH, RG)",
      "Consultar histórico do cliente",
      "Editar dados cadastrais"
    ]
  },
  {
    id: 6,
    titulo: "Controle de Aluguéis",
    arquivo: "VID-20250827-WA0007.mp4",
    descricao: "Gerencie todo o ciclo de locação: reserva, retirada, devolução e pagamentos.",
    categoria: "Operações",
    duracao: "10:30",
    nivel: "Intermediário",
    icone: <AssignmentIcon />,
    topicos: [
      "Criar nova reserva",
      "Registrar retirada do veículo",
      "Processar devolução",
      "Calcular multas e descontos",
      "Finalizar pagamento"
    ]
  }
];

const guiasRapidos = [
  {
    titulo: "Fluxo Completo de Locação",
    etapas: [
      "Cadastrar cliente (se novo)",
      "Verificar disponibilidade do veículo",
      "Criar reserva/aluguel",
      "Aplicar promoção (se aplicável)",
      "Registrar saída no controle de movimento",
      "Processar devolução e checklist",
      "Calcular valores finais (multas/descontos)",
      "Finalizar pagamento"
    ]
  },
  {
    titulo: "Relatórios e Análises",
    etapas: [
      "Acessar menu Relatórios",
      "Análise de Veículos - ROI e ocupação",
      "Resumo Financeiro - receitas e despesas",
      "Dashboard - visão geral do negócio",
      "Exportar dados para análise"
    ]
  },
  {
    titulo: "Gestão Financeira",
    etapas: [
      "Acompanhar Contas a Receber",
      "Gerenciar Contas a Pagar",
      "Registrar movimentação de caixa",
      "Consultar relatórios financeiros",
      "Identificar pendências"
    ]
  }
];

export default function Instrucoes() {
  const [busca, setBusca] = useState('');
  const [categoriaAtiva, setCategoriaAtiva] = useState('Todos');
  const [videoSelecionado, setVideoSelecionado] = useState(null);

  const categorias = ['Todos', ...new Set(tutoriais.map(t => t.categoria))];

  const tutoriaisFiltrados = useMemo(() => {
    return tutoriais.filter(tutorial => {
      const matchBusca = tutorial.titulo.toLowerCase().includes(busca.toLowerCase()) ||
                        tutorial.descricao.toLowerCase().includes(busca.toLowerCase());
      const matchCategoria = categoriaAtiva === 'Todos' || tutorial.categoria === categoriaAtiva;
      return matchBusca && matchCategoria;
    });
  }, [busca, categoriaAtiva]);

  const progressoTreinamento = 0; // Pode ser integrado com localStorage para salvar progresso

  return (
    <Container maxWidth="xl" sx={{ mt: 3, mb: 4, px: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <SchoolIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Central de Treinamento
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Aprenda a utilizar todas as funcionalidades do sistema
            </Typography>
          </Box>
        </Box>

        {/* Progresso do Treinamento */}
        <Paper sx={{ p: 2, borderRadius: 2, bgcolor: 'primary.50' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              Seu Progresso
            </Typography>
            <Typography variant="subtitle2" color="primary">
              {progressoTreinamento}% Concluído
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={progressoTreinamento} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Paper>
      </Box>

      {/* Busca e Filtros */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Buscar tutorial..."
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Tabs
              value={categoriaAtiva}
              onChange={(e, newValue) => setCategoriaAtiva(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {categorias.map(cat => (
                <Tab key={cat} label={cat} value={cat} />
              ))}
            </Tabs>
          </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {/* Lista de Tutoriais */}
        <Grid item xs={12} lg={8}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Tutoriais em Vídeo
          </Typography>
          
          <Grid container spacing={3}>
            {tutoriaisFiltrados.map((tutorial) => (
              <Grid item xs={12} md={6} key={tutorial.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'all 0.3s',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 6
                    }
                  }}
                  onClick={() => setVideoSelecionado(tutorial)}
                >
                  <Box sx={{ position: 'relative' }}>
                    <CardMedia
                      component="video"
                      height="200"
                      src={`/assets/videos/${tutorial.arquivo}`}
                      sx={{ bgcolor: 'grey.900' }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <PlayCircleOutlineIcon sx={{ fontSize: 60, color: 'white' }} />
                    </Box>
                    <Chip
                      label={tutorial.duracao}
                      size="small"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        right: 8,
                        bgcolor: 'rgba(0,0,0,0.7)',
                        color: 'white'
                      }}
                    />
                  </Box>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box sx={{ color: 'primary.main' }}>{tutorial.icone}</Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                        {tutorial.titulo}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {tutorial.descricao}
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip label={tutorial.categoria} size="small" color="primary" variant="outlined" />
                      <Chip 
                        label={tutorial.nivel} 
                        size="small" 
                        color={tutorial.nivel === 'Iniciante' ? 'success' : 'warning'}
                      />
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {tutoriaisFiltrados.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Nenhum tutorial encontrado com os filtros selecionados.
            </Alert>
          )}
        </Grid>

        {/* Guias Rápidos */}
        <Grid item xs={12} lg={4}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            <TimelineIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
            Guias Rápidos
          </Typography>

          <Stack spacing={2}>
            {guiasRapidos.map((guia, idx) => (
              <Accordion key={idx} defaultExpanded={idx === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography fontWeight={600}>{guia.titulo}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <List dense>
                    {guia.etapas.map((etapa, i) => (
                      <ListItem key={i} sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={etapa}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>

          <Divider sx={{ my: 3 }} />

          {/* Dicas */}
          <Paper sx={{ p: 2, bgcolor: 'info.50', borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              💡 Dica de Uso
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Comece pelos tutoriais de "Cadastros Básicos" antes de avançar para funcionalidades mais complexas.
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Modal de Vídeo */}
      {videoSelecionado && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0,0,0,0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3
          }}
          onClick={() => setVideoSelecionado(null)}
        >
          <Box
            sx={{ maxWidth: 1200, width: '100%' }}
            onClick={(e) => e.stopPropagation()}
          >
            <Paper sx={{ p: 3, borderRadius: 2 }}>
              <Typography variant="h5" fontWeight={600} sx={{ mb: 2 }}>
                {videoSelecionado.titulo}
              </Typography>
              <video 
                controls 
                autoPlay
                width="100%" 
                style={{ borderRadius: 8, maxHeight: '70vh' }}
              >
                <source src={`/assets/videos/${videoSelecionado.arquivo}`} type="video/mp4" />
                Seu navegador não suporta vídeo.
              </video>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Tópicos Abordados:
                </Typography>
                <List dense>
                  {videoSelecionado.topicos.map((topico, i) => (
                    <ListItem key={i} sx={{ py: 0.5 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        <CheckCircleIcon sx={{ fontSize: 18, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText primary={topico} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Paper>
          </Box>
        </Box>
      )}
    </Container>
  );
}